// Copyright © Veeam Software Group GmbH

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Subject } from 'rxjs';

import type { FC } from 'react';
import type { RbacScope } from 'services/rbac';

import { getDefaultScope, rbacService } from 'services/rbac';

export interface RbacContext {
    isOperator?: boolean;
    currentScope: RbacScope;
    currentScopeLabel: string;
    changeScope(): void;
}

const RbacContextReact = createContext<RbacContext>({
    isOperator: false,
    currentScope: getDefaultScope(),
    currentScopeLabel: '',
    changeScope: () => { },
});

export const useRbac = (): RbacContext => useContext(RbacContextReact);

const onScopeChangeRequest = new Subject<{}>();

const getCurrentScopeLabel = (scope: RbacScope): string => scope.id === rbacService.info.operatorId ? '' : scope.title;

export const RbacContextProvider: FC = ({ children }) => {
    const [isOperator, setOperator] = useState(rbacService.info.isOperator);
    const [currentScope, setCurrentScope] = useState<RbacScope>(rbacService.info.scope);
    const [currentScopeLabel, setCurrentScopeLabel] = useState<string>(getCurrentScopeLabel(rbacService.info.scope));

    useEffect(() => {
        const subscription = rbacService.onScopeChanged.subscribe((newScope) => {
            setCurrentScope(newScope);
            setCurrentScopeLabel(getCurrentScopeLabel(newScope));
        });

        rbacService.fetchOperatorInfo()
            .then((info) => {
                if (info) {
                    setOperator(info.isOperator);
                    setCurrentScope(info.scope);
                    setCurrentScopeLabel(getCurrentScopeLabel(info.scope));
                }
            });

        return subscription.unsubscribe;
    }, []);

    const changeScope = () => onScopeChangeRequest.next({});

    const rbacContextValue = useMemo(() => ({
        isOperator,
        currentScope,
        currentScopeLabel,
        changeScope,
    }), [isOperator, currentScope, currentScopeLabel, changeScope]);

    return (
        <RbacContextReact.Provider value={rbacContextValue}>
            {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
            <>
                {children}
            </>
        </RbacContextReact.Provider>
    );
};
